import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useAuth from '@/hooks/useAuth';

interface UseAuthRedirectProps {
  /** Whether this is for Shopify authentication flow */
  isShopify?: boolean;
  /** Custom redirect path after successful authentication */
  redirectPath?: string;
}

/**
 * Simplified declarative navigation configuration
 * This is a more practical, easier-to-adopt improvement
 */
interface NavigationConfig {
  isShopify: boolean;
  next: string | null;
  from: string | null;
  defaultPath: string;
  activationKey?: string;
  locationState?: any;
}

/**
 * Determine redirect destination using a clear priority system
 * Returns [path, state] tuple or 'shopify' for special handling
 */
const getRedirectDestination = (
  config: NavigationConfig,
): ['shopify'] | [string, Record<string, any>?] => {
  // Priority 1: Shopify flow
  if (config.isShopify) {
    return ['shopify'];
  }

  // Priority 2: URL 'next' parameter
  if (config.next) {
    return [config.next];
  }

  // Priority 3: 'from' state (where user was before login)
  if (config.from) {
    return [config.from, config.locationState];
  }

  // Priority 4: Default fallback
  return [
    config.defaultPath,
    config.activationKey
      ? { activationKeyFromLogin: config.activationKey }
      : undefined,
  ];
};

/**
 * Custom hook to handle authentication redirects using a simplified declarative approach.
 * Improves maintainability by separating navigation logic from execution.
 */
export const useAuthRedirect = ({
  isShopify = false,
  redirectPath = '/',
}: UseAuthRedirectProps) => {
  const { user, redirectToShopify } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Extract navigation parameters (memoized for performance)
  const navigationConfig = useMemo((): NavigationConfig => {
    const searchParams = new URLSearchParams(location.search);
    return {
      isShopify,
      next: searchParams.get('next'),
      from: location.state?.from?.pathname,
      defaultPath: redirectPath,
      activationKey: location.state?.activationKey,
      locationState: location.state,
    };
  }, [isShopify, location.search, location.state, redirectPath]);

  useEffect(() => {
    if (!user) {
      setIsRedirecting(false);
      return;
    }

    setIsRedirecting(true);

    const destination = getRedirectDestination(navigationConfig);

    if (destination[0] === 'shopify') {
      redirectToShopify();
    } else {
      const [path, state] = destination;
      navigate(path, { replace: true, state });
    }
  }, [user, navigationConfig, navigate, redirectToShopify]);

  // Return whether user is authenticated (useful for conditional rendering)
  return {
    isAuthenticated: !!user,
    isRedirecting: !!user && isRedirecting,
  };
};

export default useAuthRedirect;
